// The Go runtime library, emitted verbatim beneath the generated program by
// the Go backend (see ./gogen.ts). Two hand-written runtimes: a fine-grained
// TL2-style STM runtime, and an in-place lock runtime used when the LockPlan
// proves every transaction can be pessimistic.

// ---------------------------------------------------------------------------
// The Go STM runtime (TL2-style, fine-grained). Emitted verbatim.
// ---------------------------------------------------------------------------

export const RUNTIME_MVCC = `// ===================== STM runtime (TL2-style) =====================
//
// Each heap object is a free-floating *Object with its own lock + version.
// There is no central store: the only shared state is one global version clock.
// Reads are lock-free validated loads; a commit locks just the written objects,
// bumps the clock once, validates the read set, and publishes.

// Every heap object points directly at its current record via an atomic
// pointer. The version lives INSIDE the record (its first field, __ver), so a
// commit publishes a new value and its version in a single pointer swap — no
// separate version cell is allocated. Records are immutable once published, so a
// reader's atomic pointer load always yields a self-consistent (version, value)
// pair with no lock and no seqlock retry.
type recHdr struct {
	__ver uint64
}

type Object struct {
	id   uint64
	tag  uint16
	lock atomic.Uint64
	val  unsafe.Pointer // *record; every record has __ver uint64 as its first field
}

var gClock atomic.Uint64
var objSeq atomic.Uint64
var __commits atomic.Uint64
var __aborts atomic.Uint64
var wg sync.WaitGroup

func recVer(p unsafe.Pointer) uint64 { return (*recHdr)(p).__ver }

func newObject(tag uint16, p unsafe.Pointer) *Object {
	o := &Object{id: objSeq.Add(1), tag: tag}
	atomic.StorePointer(&o.val, p) // records zero-init __ver, so this is version 0
	return o
}

func (o *Object) lockSpin() {
	for !o.lock.CompareAndSwap(0, 1) {
		runtime.Gosched()
	}
}
func (o *Object) unlock() { o.lock.Store(0) }

type txnAbort struct{}

// retrySignal unwinds a \`retry\` up to the enclosing Run, which parks the thread
// on the aborted attempt's read set instead of re-running immediately. A single
// shared token value (\`__retryToken\`) is panicked so a retry allocates nothing.
type retrySignal struct{}

var __retryToken = retrySignal{}
var __retries atomic.Uint64

// A parked \`retry\`: the read set the aborted attempt accumulated, plus a
// one-shot channel the waker signals. The thread blocks on \`ch\` until a
// committed write touches one of \`reads\`' objects (see wakeRetryWaiters).
type retryWaiter struct {
	reads []rEntry
	ch    chan struct{}
}

var retryMu sync.Mutex
var retryWaiters = map[*retryWaiter]struct{}{}

// retryStale reports whether any object in the read set has moved past the
// version the attempt saw (or is locked mid-commit) — i.e. the write that would
// wake us has *already* landed. Called under retryMu, so it composes with a
// committer's publish-then-wake ordering to rule out a lost wakeup: a commit
// either publishes before we snapshot here (we see it stale and re-run) or scans
// the waiter map after we register (it wakes us).
func retryStale(reads []rEntry) bool {
	for i := range reads {
		r := reads[i]
		if r.o.lock.Load() != 0 || recVer(atomic.LoadPointer(&r.o.val)) != r.ver {
			return true
		}
	}
	return false
}

// retryPark blocks until a committed write touches an object the aborted attempt
// read, then returns so Run re-runs the body. Mirrors the interpreter's
// validate-before-park: if the read set is already stale, return at once rather
// than wait for a write that has already happened.
func retryPark(reads []rEntry) {
	w := &retryWaiter{ch: make(chan struct{}, 1)}
	w.reads = append(w.reads, reads...) // own copy: the Txn's read slice is reused
	retryMu.Lock()
	if retryStale(w.reads) {
		retryMu.Unlock()
		return
	}
	retryWaiters[w] = struct{}{}
	retryMu.Unlock()
	<-w.ch
}

// wakeRetryWaiters wakes every parked \`retry\` whose read set overlaps this
// commit's write set (by object identity — versions are checked on re-run). Must
// be called AFTER the writes are published, so a woken thread re-reads the new
// values. A simple scan of the parked list; fine at this scale (see the design
// note's wake-index options).
func wakeRetryWaiters(writes []wEntry) {
	if len(writes) == 0 {
		return
	}
	retryMu.Lock()
	for w := range retryWaiters {
		if readsOverlapWrites(w.reads, writes) {
			delete(retryWaiters, w)
			w.ch <- struct{}{} // buffered (cap 1) and one-shot: never blocks
		}
	}
	retryMu.Unlock()
}

func readsOverlapWrites(reads []rEntry, writes []wEntry) bool {
	for i := range reads {
		for j := range writes {
			if reads[i].o == writes[j].o {
				return true
			}
		}
	}
	return false
}

// Read and write sets are kept as slices, not maps: a transaction usually
// touches only a handful of objects, so a linear scan beats allocating and
// hashing a map on every attempt. Txn structs are pooled and their slices are
// reused (reset to len 0) across attempts and transactions, so the steady state
// allocates nothing for bookkeeping.
type rEntry struct {
	o   *Object
	ver uint64
}
type wEntry struct {
	o   *Object
	val unsafe.Pointer
}

type Txn struct {
	rv     uint64
	reads  []rEntry
	writes []wEntry
	locked bool // true in a pessimistic (2PL) transaction: footprint is pre-locked
}

// get reads addr, recording the dependency. Own buffered writes win. Outside a
// transaction (tx == nil) it is a plain lock-free load of the latest record.
//
// The record swap is atomic so we never see a torn value, but that alone is not
// enough for opacity: a committer bumps the global clock BEFORE it publishes, so
// while an object is locked its visible record can be older than the clock
// implies. The TL2 read protocol closes that window — verify the object is
// unlocked, load the record, verify it is still unlocked and unchanged — so we
// never build a snapshot on a value another transaction has already superseded.
func (o *Object) get(tx *Txn) unsafe.Pointer {
	if tx == nil {
		return atomic.LoadPointer(&o.val)
	}
	for i := range tx.writes {
		if tx.writes[i].o == o {
			return tx.writes[i].val
		}
	}
	if tx.locked {
		// Pessimistic transaction: we already hold this object's lock (it's in the
		// footprint), so its value is stable. No versioning, no abort.
		return atomic.LoadPointer(&o.val)
	}
	if o.lock.Load() != 0 {
		panic(txnAbort{})
	}
	p := atomic.LoadPointer(&o.val)
	v := recVer(p)
	if o.lock.Load() != 0 || v > tx.rv {
		panic(txnAbort{}) // locked mid-commit, or newer than our snapshot
	}
	tx.reads = append(tx.reads, rEntry{o, v})
	return p
}

// set buffers a write inside a transaction, or auto-commits a single-object
// write outside one (read-committed, last-writer-wins).
func (o *Object) set(tx *Txn, p unsafe.Pointer) {
	if tx == nil {
		o.lockSpin()
		wv := gClock.Add(1)
		(*recHdr)(p).__ver = wv
		atomic.StorePointer(&o.val, p)
		o.unlock()
		return
	}
	for i := range tx.writes {
		if tx.writes[i].o == o {
			tx.writes[i].val = p
			return
		}
	}
	tx.writes = append(tx.writes, wEntry{o, p})
}

// sortWrites orders the write set by object id (deadlock-free lock ordering).
// A tiny write set (the usual case) uses insertion sort — cheaper than
// sort.Slice, which allocates a closure and uses reflection. A big one — e.g. a
// transaction that bulk-writes a collection, whose entries are each a separately
// transacted cell — uses an in-place heap sort so commit stays O(n log n)
// instead of insertion sort's O(n^2).
func sortWrites(ws []wEntry) {
	if len(ws) < 16 {
		for i := 1; i < len(ws); i++ {
			w := ws[i]
			j := i - 1
			for j >= 0 && ws[j].o.id > w.o.id {
				ws[j+1] = ws[j]
				j--
			}
			ws[j+1] = w
		}
		return
	}
	n := len(ws)
	for i := n/2 - 1; i >= 0; i-- {
		siftDownWrites(ws, i, n)
	}
	for end := n - 1; end > 0; end-- {
		ws[0], ws[end] = ws[end], ws[0]
		siftDownWrites(ws, 0, end)
	}
}

func siftDownWrites(ws []wEntry, lo, hi int) {
	for {
		child := 2*lo + 1
		if child >= hi {
			return
		}
		if child+1 < hi && ws[child].o.id < ws[child+1].o.id {
			child++
		}
		if ws[lo].o.id >= ws[child].o.id {
			return
		}
		ws[lo], ws[child] = ws[child], ws[lo]
		lo = child
	}
}

func (tx *Txn) written(o *Object) bool {
	for i := range tx.writes {
		if tx.writes[i].o == o {
			return true
		}
	}
	return false
}

func (tx *Txn) commit() bool {
	if len(tx.writes) == 0 {
		return true // read-only: reads were validated incrementally (opacity)
	}
	// deadlock-free acquisition: lock the write set in a global (id) order.
	sortWrites(tx.writes)
	for i := range tx.writes {
		tx.writes[i].o.lockSpin()
	}
	wv := gClock.Add(1)
	ok := true
	// Validate the whole read set. (No rv+1==wv "nobody committed since we began"
	// fast path here: because aborted commits also advance the clock, rv can sit
	// above the last *published* version, so that shortcut can wrongly skip a real
	// conflict. The read set is tiny, so always validating is cheap.)
	for i := range tx.reads {
		r := tx.reads[i]
		// Every read must be validated, INCLUDING read-modify-write objects:
		// a stale read of an object we also write is still a lost update. For
		// objects we wrote we already hold the lock, so only check the version;
		// for read-only objects a set lock means another committer owns it.
		if tx.written(r.o) {
			if recVer(atomic.LoadPointer(&r.o.val)) != r.ver {
				ok = false
				break
			}
		} else if r.o.lock.Load() != 0 || recVer(atomic.LoadPointer(&r.o.val)) != r.ver {
			ok = false
			break
		}
	}
	if !ok {
		for i := range tx.writes {
			tx.writes[i].o.unlock()
		}
		return false
	}
	// Stamp each new record with the commit version and publish it atomically.
	for i := range tx.writes {
		(*recHdr)(tx.writes[i].val).__ver = wv
		atomic.StorePointer(&tx.writes[i].o.val, tx.writes[i].val)
	}
	for i := range tx.writes {
		tx.writes[i].o.unlock()
	}
	wakeRetryWaiters(tx.writes) // published above: a woken \`retry\` re-reads new values
	return true
}

var txnPool = sync.Pool{New: func() any { return &Txn{} }}

// Run executes body as a transaction. A non-nil outer tx means we're already in
// a transaction, so we flatten (nested txn joins the enclosing one). Otherwise
// we run optimistically and retry on conflict.
func Run(outer *Txn, body func(*Txn)) {
	if outer != nil {
		body(outer)
		return
	}
	tx := txnPool.Get().(*Txn)
	tx.locked = false
	for {
		tx.rv = gClock.Load()
		tx.reads = tx.reads[:0]
		tx.writes = tx.writes[:0]
		switch runBody(tx, body) {
		case bodyOk:
			if tx.commit() {
				__commits.Add(1)
				txnPool.Put(tx)
				return
			}
			__aborts.Add(1) // read set moved under us: re-run immediately
		case bodyRetry:
			// \`retry\`: abandon this attempt (buffered writes dropped) and park on
			// the read set. retryPark returns when a committed write overlaps it (or
			// at once if one already has), then we re-run from the top.
			__retries.Add(1)
			retryPark(tx.reads)
		default: // bodyAbort — a conflicting read tripped opacity mid-attempt
			__aborts.Add(1)
		}
	}
}

// orElse is the select / composable-blocking primitive (see
// docs/design/concurrency-via-retry.md). Run branch a; if it \`retry\`s, roll back
// ONLY a's tentative writes — its reads stay in tx.reads, so they remain in the wake
// set and are still validated at commit — then run b. If b also retries, the panic
// propagates to Run, which parks on the union of both branches' reads. If a succeeds,
// b never runs. Reads accumulate, writes roll back: that asymmetry is what gives Go's
// "take one, leave the rest" — a losing branch's provisional writes (e.g. a channel
// cursor bump) are undone by the rollback, as a general STM combinator rather than
// channel-specific runtime magic.
func orElse[T any](tx *Txn, a, b func(*Txn) T) T {
	// Checkpoint the write set BY VALUE: a branch may overwrite an entry buffered
	// before the orElse (own-writes-win updates in place), which a length-only
	// checkpoint couldn't undo. tx.reads is deliberately NOT snapshotted.
	save := append([]wEntry(nil), tx.writes...)
	if res, blocked := orElseTry(tx, a); !blocked {
		return res
	}
	tx.writes = save // undo a's tentative writes; keep the accumulated read set
	return b(tx)     // a second \`retry\` propagates to Run, which parks on the union
}

// orElseTry runs f and reports whether it \`retry\`d (parked). A txnAbort or any other
// panic — a real error — propagates untouched: orElse recovers from *blocking* only,
// never from errors (it is \`retry\`'s handler, not a \`catch\`).
func orElseTry[T any](tx *Txn, f func(*Txn) T) (res T, blocked bool) {
	defer func() {
		if r := recover(); r != nil {
			if _, ok := r.(retrySignal); ok {
				blocked = true
				return
			}
			panic(r)
		}
	}()
	return f(tx), false
}

// ----- pessimistic (2PL) strategy -----
//
// When the compiler can enumerate a transaction's footprint statically, it emits
// one of these instead of Run: lock the footprint (in object-id order, so it is
// deadlock-free and interleaves safely with OCC commits, which lock in the same
// order), run the body exactly once — no retry, so side effects are safe — then
// publish the buffered writes with a fresh version and unlock. An OCC reader that
// touches a locked object simply aborts and retries, so the two strategies mix.
func runLocked(tx *Txn, body func(*Txn)) {
	tx.locked = true
	tx.reads = tx.reads[:0]
	tx.writes = tx.writes[:0]
	body(tx)
	if len(tx.writes) > 0 {
		wv := gClock.Add(1)
		for i := range tx.writes {
			(*recHdr)(tx.writes[i].val).__ver = wv
			atomic.StorePointer(&tx.writes[i].o.val, tx.writes[i].val)
		}
		wakeRetryWaiters(tx.writes) // a 2PL commit can wake an OCC \`retry\` too
	}
	tx.locked = false
	__commits.Add(1)
}

func RunLocked1(outer *Txn, o *Object, body func(*Txn)) {
	if outer != nil {
		body(outer)
		return
	}
	tx := txnPool.Get().(*Txn)
	o.lockSpin()
	runLocked(tx, body)
	o.unlock()
	txnPool.Put(tx)
}

func RunLocked2(outer *Txn, o1, o2 *Object, body func(*Txn)) {
	if outer != nil {
		body(outer)
		return
	}
	a, b := o1, o2
	if a.id > b.id {
		a, b = b, a
	}
	tx := txnPool.Get().(*Txn)
	a.lockSpin()
	dup := a == b // transfer(x, x): lock once
	if !dup {
		b.lockSpin()
	}
	runLocked(tx, body)
	if !dup {
		b.unlock()
	}
	a.unlock()
	txnPool.Put(tx)
}

func RunLockedN(outer *Txn, objs []*Object, body func(*Txn)) {
	if outer != nil {
		body(outer)
		return
	}
	// sort by id and dedup, then lock in order.
	for i := 1; i < len(objs); i++ {
		o := objs[i]
		j := i - 1
		for j >= 0 && objs[j].id > o.id {
			objs[j+1] = objs[j]
			j--
		}
		objs[j+1] = o
	}
	tx := txnPool.Get().(*Txn)
	var locked []*Object
	for _, o := range objs {
		if len(locked) > 0 && locked[len(locked)-1] == o {
			continue // dedup
		}
		o.lockSpin()
		locked = append(locked, o)
	}
	runLocked(tx, body)
	for i := len(locked) - 1; i >= 0; i-- {
		locked[i].unlock()
	}
	txnPool.Put(tx)
}

const (
	bodyOk    = 0 // ran to the end: try to commit
	bodyAbort = 1 // opacity abort (txnAbort): re-run immediately
	bodyRetry = 2 // \`retry\`: park on the read set, re-run when it changes
)

func runBody(tx *Txn, body func(*Txn)) (outcome int) {
	defer func() {
		if r := recover(); r != nil {
			switch r.(type) {
			case txnAbort:
				outcome = bodyAbort
			case retrySignal:
				outcome = bodyRetry
			default:
				panic(r)
			}
		}
	}()
	body(tx)
	return bodyOk
}

func spawn(f func()) {
	wg.Add(1)
	go func() {
		defer wg.Done()
		f()
	}()
}

// (Futures are no longer native: \`Future<T>\`/\`spawn\`/\`await\`/\`race\` are ordinary
// STM library types built on \`retry\` and the fire-and-forget \`go\` statement — see
// src/stdlib/concurrency.stm. The old native future/race runtime helpers
// are gone; blocking is \`retry\`/\`retryPark\` above.)

// ----- collections: entries live in the same flat, transacted key space ------
//
// Conceptually there is ONE key space the STM transacts over. A normal object is
// a key (/obj => record); a collection entry is just another key (/coll/k => an
// entry cell). Both are versioned, copy-on-write cells validated through the SAME
// read/write sets — so membership/mapping is per-ENTRY MVCC: a write copies only
// that entry (never the whole map), and conflict detection is per entry (two
// transactions touching different keys don't conflict).
//
// A Set and a Dict are the SAME shape: a namespace (collRec) mapping a KEY to
// that entry's cell, an ordinary *Object holding a memCell. The key is 'any', so
// it works for object identity (*Object, compared by pointer) and for primitive
// values (int64/float64/bool/string, compared by value) alike. A Set stores no
// value (membership only); a Dict stores the mapped value in the same cell. All
// the STM behaviour — copy-on-write, own-writes-win, snapshot isolation, commit
// locking, validation — is inherited from the object layer with no bespoke code.
// (This is the template a List would follow too, keyed by index.)
//
// The cell map only grows: a cell is materialized on first touch — INCLUDING by
// a membership miss (contains / has / remove), so that an "absent" read is
// recorded and conflict-detected (a concurrent add of that key aborts the
// reader). That makes point membership reads serializable — no write skew, and
// has()->get()->set() read-modify-writes are safe — at the cost of a cell
// allocation per never-seen key probed. RANGE reads (iteration / size) are
// serializable too: they read the collection's key-set version (keyVer, below),
// and a new-key insert bumps it, so a range read conflicts with a concurrent
// insert of a key it couldn't have seen — closing the last write-skew gap.

type memCell struct {
	__ver   uint64
	present bool
	val     any // Dict: the mapped value; Set: nil
}

// The iteration log. A collection's entries are enumerated through an append-only
// log of (key, cell) pairs rather than by ranging the index map — so a scan is a
// plain indexed walk with no lock and no snapshot copy of the key set, and it can
// run concurrently with inserts. The log is chunked: each chunk is a fixed array
// that never moves once published, and the small array of chunk pointers is
// swapped atomically on growth, so a reader that loaded an older view still walks
// a valid prefix (see __collKeys).
const logChunkSize = 4096

type logEntry struct {
	key  any
	cell *Object
}

type logChunk = [logChunkSize]logEntry

// collRec is a collection's namespace. It is created once and never swapped. The
// index map (key -> cell) is guarded by mu for point lookup and insertion; the
// per-cell values are versioned by the object layer. Keys are 'any': an *Object
// (object identity) or a primitive (by value). The collection only ever grows —
// a removed entry is a present=false tombstone in its cell, never erased — so a
// log slot, once published, stays valid forever.
//
// Iteration and size read the log lock-free via count + chunks (both atomic):
// count is the number of published entries (a scan's upper bound, so it can
// pre-size the result), and chunks is the published chunk-pointer array. An
// insert appends under mu, filling the slot and publishing its chunk BEFORE
// bumping count, so a reader that observes the count sees a fully-written entry.
//
// keyVer is the collection's KEY-SET version — an ordinary versioned *Object.
// Iteration and size read it, and inserting a NEW key bumps it, so a range read
// is conflict-detected against a concurrent insert of a key it couldn't have
// seen (serializable iteration/size — no phantom write skew).
type collRec struct {
	mu     sync.RWMutex
	index  map[any]*Object             // key -> its entry cell (point lookup, guarded by mu)
	chunks atomic.Pointer[[]*logChunk] // published chunk-pointer array (read lock-free)
	count  atomic.Int64                // number of published log entries (read lock-free)
	keyVer *Object                     // key-set version: read by iterate/size, bumped on new-key insert
	iterFn func(*Txn, *Object) any     // "iterate me as an Iterable": build my cursor. Set at
	//                                    construction (element type known there); read by the
	//                                    Iterable.iter dispatch. nil if never used as an Iterable.
}

func newCollRec() *collRec {
	r := &collRec{index: make(map[any]*Object), keyVer: newObject(0, unsafe.Pointer(&memCell{}))}
	empty := []*logChunk{}
	r.chunks.Store(&empty)
	return r
}

// newCollRecIter makes a Set/Dict namespace that carries its element-specific
// cursor-maker (see collRec.iterFn), installed where the element type is known.
func newCollRecIter(iterFn func(*Txn, *Object) any) *collRec {
	r := newCollRec()
	r.iterFn = iterFn
	return r
}

func collNS(s *Object) *collRec { return (*collRec)(atomic.LoadPointer(&s.val)) }

// bumpKeys advances the key-set version. It is a BLIND write (never read here),
// so concurrent inserts don't conflict with EACH OTHER — only a concurrent
// iterate/size, which reads keyVer, is invalidated and retries.
func (r *collRec) bumpKeys(tx *Txn) { r.keyVer.set(tx, unsafe.Pointer(&memCell{})) }

// lookup returns the existing cell for key, or nil if the key was never touched.
func (r *collRec) lookup(key any) *Object {
	r.mu.RLock()
	c := r.index[key]
	r.mu.RUnlock()
	return c
}

// cell returns key's entry cell, materializing an absent one on first use
// (double-checked so concurrent first-touches share a single cell). A newly
// materialized cell is appended to the iteration log.
func (r *collRec) cell(key any) *Object {
	if c := r.lookup(key); c != nil {
		return c
	}
	r.mu.Lock()
	c := r.index[key]
	if c == nil {
		c = newObject(0, unsafe.Pointer(&memCell{}))
		r.index[key] = c
		r.appendLog(key, c)
	}
	r.mu.Unlock()
	return c
}

// appendLog publishes one (key, cell) entry to the iteration log. Called under
// mu, so writers are serialized. The slot is filled and its chunk published
// BEFORE count is bumped — the release that pairs with a scanner's acquiring load
// of count, so a reader that sees the new count sees a fully-written entry. A
// published chunk never moves; growth only swaps the small chunk-pointer array.
func (r *collRec) appendLog(key any, c *Object) {
	n := int(r.count.Load())
	ci, off := n/logChunkSize, n%logChunkSize
	chunks := *r.chunks.Load()
	if ci == len(chunks) {
		nc := make([]*logChunk, len(chunks)+1)
		copy(nc, chunks)
		nc[ci] = new(logChunk)
		r.chunks.Store(&nc)
		chunks = nc
	}
	chunks[ci][off] = logEntry{key: key, cell: c}
	r.count.Add(1)
}

// ----- Set operations (membership: the cell's val is unused) -----

func __setAdd(tx *Txn, s *Object, x any) {
	ns := collNS(s)
	c := ns.cell(x)
	if (*memCell)(c.get(tx)).present {
		return // already a member in this transaction's view
	}
	c.set(tx, unsafe.Pointer(&memCell{present: true}))
	ns.bumpKeys(tx) // a new member changes the key-set
}

func __setRemove(tx *Txn, s *Object, x any) bool {
	c := collNS(s).cell(x) // materialize so an absent remove still records the read
	if !(*memCell)(c.get(tx)).present {
		return false // absent in this view
	}
	c.set(tx, unsafe.Pointer(&memCell{present: false}))
	return true
}

func __setContains(tx *Txn, s *Object, x any) bool {
	// Materialize the entry cell even on a miss, so this read — absent or not —
	// is recorded and conflict-detected: a concurrent add of x invalidates it and
	// forces a retry. Costs a cell allocation per never-seen key probed.
	return (*memCell)(collNS(s).cell(x).get(tx)).present
}

// ----- Dict operations (key -> value: the cell carries the value) -----

func __dictSet(tx *Txn, d *Object, k any, v any) {
	ns := collNS(d)
	ns.cell(k).set(tx, unsafe.Pointer(&memCell{present: true, val: v}))
	// Conservative: a blind set can't tell a new key from an overwrite without
	// reading the cell, so we bump on every set. A value overwrite therefore may
	// conflict with a concurrent key iteration (safe, occasionally over-strict);
	// refine by reading the cell's present flag first if that shows up as a problem.
	ns.bumpKeys(tx)
}

func __dictGet(tx *Txn, d *Object, k any) any {
	c := collNS(d).lookup(k)
	if c == nil {
		panic("get: key not present")
	}
	m := (*memCell)(c.get(tx))
	if !m.present {
		panic("get: key not present")
	}
	return m.val
}

// __dictGetOpt is the user-facing lookup: it never panics on a miss, returning
// ok=false so the caller can build an Option. Reads only the key's own cell (like
// __dictGet), so a get of one key never conflicts with a get/set of another. A
// materializing lookup (cell, not lookup) records the absent read, so a miss
// conflicts with a concurrent insert of that key — the same has()-semantics.
func __dictGetOpt(tx *Txn, d *Object, k any) (any, bool) {
	m := (*memCell)(collNS(d).cell(k).get(tx))
	if !m.present {
		return nil, false
	}
	return m.val, true
}

func __dictHas(tx *Txn, d *Object, k any) bool {
	// Materialize on a miss so an "absent" read is recorded (see __setContains):
	// this also makes has()->get()->set() read-modify-writes safe, since a
	// first-time key's has-miss now conflicts with a concurrent insert.
	return (*memCell)(collNS(d).cell(k).get(tx)).present
}

func __dictRemove(tx *Txn, d *Object, k any) bool {
	c := collNS(d).cell(k) // materialize so an absent remove still records the read
	if !(*memCell)(c.get(tx)).present {
		return false
	}
	c.set(tx, unsafe.Pointer(&memCell{present: false}))
	return true
}

// ----- List operations (an ordered, index-keyed sequence) -----
//
// A List reuses the same collRec, keying each element by its int64 index. Where a
// Set/Dict bump the key-set version BLINDLY, a List's append READS and then WRITES
// the length cell (keyVer, repurposed to carry the element count) — a
// read-modify-write, so two concurrent appends read the same length, target the
// same index, and conflict: one aborts and retries at the next index. The order of
// a list is therefore always well-defined. There is no remove, so the live indices
// stay a contiguous 0..len-1 and the append-only log is already in index order.

// __listLen reads the list's length from its length cell (keyVer.val), recording
// the read so a concurrent append is conflict-detected. A nil value means empty.
func __listLen(tx *Txn, ns *collRec) int64 {
	m := (*memCell)(ns.keyVer.get(tx))
	if m.val == nil {
		return 0
	}
	return m.val.(int64)
}

func __listAppend(tx *Txn, l *Object, v any) {
	ns := collNS(l)
	n := __listLen(tx, ns) // reads the length cell — the read-modify-write conflict point
	ns.cell(n).set(tx, unsafe.Pointer(&memCell{present: true, val: v}))
	ns.keyVer.set(tx, unsafe.Pointer(&memCell{val: n + 1}))
}

// __listLit builds a list literal ([1, 2, 3]): a fresh list already holding vs, in
// index order. The list is unreachable by any other thread while it is built —
// nothing else holds a reference yet — so the elements go straight into their index
// cells and the length cell is written ONCE, where __listAppend read-modify-writes
// it per element. Same contents, one structural write.
func __listLit(tx *Txn, tag uint16, iterFn func(*Txn, *Object) any, vs ...any) *Object {
	l := newObject(tag, unsafe.Pointer(newCollRecIter(iterFn)))
	ns := collNS(l)
	for i, v := range vs {
		ns.cell(int64(i)).set(tx, unsafe.Pointer(&memCell{present: true, val: v}))
	}
	if len(vs) > 0 {
		ns.keyVer.set(tx, unsafe.Pointer(&memCell{val: int64(len(vs))}))
	}
	return l
}

// __dictLit builds a dict literal ({"a": 1}): a fresh dict holding kvs, given as a
// flat key, value, … run (Go has no tuple to pass pairs as). Like __listLit it is
// built in bulk — the dict is unreachable by any other thread while it is filled, so
// the entries go straight into their cells and the key-set version is bumped once,
// where __dictSet bumps on every set. A repeated key overwrites, as it would there.
func __dictLit(tx *Txn, tag uint16, iterFn func(*Txn, *Object) any, kvs ...any) *Object {
	d := newObject(tag, unsafe.Pointer(newCollRecIter(iterFn)))
	ns := collNS(d)
	for i := 0; i+1 < len(kvs); i += 2 {
		ns.cell(kvs[i]).set(tx, unsafe.Pointer(&memCell{present: true, val: kvs[i+1]}))
	}
	if len(kvs) > 0 {
		ns.bumpKeys(tx)
	}
	return d
}

// __setLit builds a set literal ({1, 2}): a fresh set holding vs. A set's element IS
// its cell key (the cell itself holds nothing), so a repeated element collapses into
// one entry. Bulk-built like the other literals — the set is unreachable by any other
// thread while it is filled, so the key-set version is bumped once, where __setAdd
// bumps per new member.
func __setLit(tx *Txn, tag uint16, iterFn func(*Txn, *Object) any, vs ...any) *Object {
	s := newObject(tag, unsafe.Pointer(newCollRecIter(iterFn)))
	ns := collNS(s)
	for _, v := range vs {
		ns.cell(v).set(tx, unsafe.Pointer(&memCell{present: true}))
	}
	if len(vs) > 0 {
		ns.bumpKeys(tx)
	}
	return s
}

func __listGet(tx *Txn, l *Object, i int64) any {
	// Bounds-check by the index cell's presence, not the length: a get of an
	// existing index then reads only that one cell — never conflicting with an
	// append or with a get/set of a different index. lookup (non-materializing)
	// keeps an out-of-range probe from planting a phantom log entry.
	c := collNS(l).lookup(i)
	if c == nil {
		panic("get: index out of range")
	}
	m := (*memCell)(c.get(tx))
	if !m.present {
		panic("get: index out of range")
	}
	return m.val
}

func __listSet(tx *Txn, l *Object, i int64, v any) {
	c := collNS(l).lookup(i)
	if c == nil {
		panic("set: index out of range")
	}
	if !(*memCell)(c.get(tx)).present {
		panic("set: index out of range")
	}
	c.set(tx, unsafe.Pointer(&memCell{present: true, val: v}))
}

func __listSize(tx *Txn, l *Object) int64 {
	return __listLen(tx, collNS(l))
}

// __listValues collects a list's elements in index order (the append-only log is
// already index-ordered). It depends on the length cell so a concurrent append
// conflicts, matching iteration's serializability for Set/Dict.
func __listValues(tx *Txn, l *Object) []any {
	ns := collNS(l)
	ns.keyVer.get(tx)
	n := int(ns.count.Load())
	chunks := *ns.chunks.Load()
	out := make([]any, 0, n)
	for i := 0; i < n; i++ {
		e := &chunks[i/logChunkSize][i%logChunkSize]
		m := (*memCell)(e.cell.get(tx))
		if m.present {
			out = append(out, m.val)
		}
	}
	return out
}

// ----- shared: iteration, size, display -----

// __collKeys collects the keys present in the collection as THIS transaction
// sees them (a Set's members, a Dict's keys). It walks the append-only log
// lock-free — load count (an upper bound, so out is pre-sized), snapshot the
// chunk array, then read each cell through the normal protocol — so a scan needs
// no lock and no key-set copy, runs concurrently with inserts, and still
// conflicts with concurrent writes to any existing entry. count is loaded before
// chunks so the chunk array covers every index below count.
func __collKeys(tx *Txn, s *Object) []any {
	ns := collNS(s)
	ns.keyVer.get(tx) // depend on the key-set: a concurrent new-key insert aborts us
	n := int(ns.count.Load())
	chunks := *ns.chunks.Load()
	out := make([]any, 0, n)
	for i := 0; i < n; i++ {
		e := &chunks[i/logChunkSize][i%logChunkSize]
		if (*memCell)(e.cell.get(tx)).present {
			out = append(out, e.key)
		}
	}
	return out
}

func __collSize(tx *Txn, s *Object) int64 {
	return int64(len(__collKeys(tx, s)))
}

// __collRecLen counts a collection's present entries at the latest committed
// version, for display (__showRec / --dump), so it takes no transaction.
func __collRecLen(r *collRec) int {
	n := int(r.count.Load())
	chunks := *r.chunks.Load()
	cnt := 0
	for i := 0; i < n; i++ {
		e := &chunks[i/logChunkSize][i%logChunkSize]
		if (*memCell)(atomic.LoadPointer(&e.cell.val)).present {
			cnt++
		}
	}
	return cnt
}

// ----- misc builtins -----

// \`assert cond, msg\` — soft, like the interpreter's: it records the verdict and
// the program carries on, so a failing assertion in one goroutine does not hide
// what the others were going to do. There is no trace to record it in here, so
// the verdict goes to stderr and to the exit code, leaving stdout to the
// program's own \`print\`s.
var __assertFailed atomic.Bool

func __assert(ok bool, msg any) {
	if ok {
		return
	}
	__assertFailed.Store(true)
	text := __show(msg)
	if text == "" {
		fmt.Fprintln(os.Stderr, "assert FAILED")
		return
	}
	fmt.Fprintf(os.Stderr, "assert FAILED: %s\\n", text)
}

func __print(args ...any) {
	parts := make([]string, len(args))
	for i, a := range args {
		parts[i] = __show(a)
	}
	line := ""
	for i, p := range parts {
		if i > 0 {
			line += " "
		}
		line += p
	}
	fmt.Println(line)
}

func __sleep(sec float64) {
	if sec > 0 {
		time.Sleep(time.Duration(sec * float64(time.Second)))
	}
}

func __absI(x int64) int64 {
	if x < 0 {
		return -x
	}
	return x
}
// =================== end STM runtime ===================`;

// ---------------------------------------------------------------------------
// The in-place runtime. Used when the LockPlan proves every transaction is
// pessimistic and no object's fields are touched outside a locked transaction.
// Objects are plain mutable records behind a per-object lock; a transaction is
// literally "lock the footprint (in id order), mutate in place, unlock" — the
// same thing a hand-written per-struct mutex program does. No versions, no
// global clock, no copy-on-write, no retries.
// ---------------------------------------------------------------------------

export const RUNTIME_INPLACE = `// ===================== lock runtime (in-place) =====================

type Object struct {
	id   uint64
	tag  uint16
	lock atomic.Uint64
	rec  unsafe.Pointer // *record, mutated in place under lock
}

// Txn is a marker only: in-place transactions carry no read/write set. The
// generated body reads and writes record fields directly (it holds the locks).
type Txn struct{}

var objSeq atomic.Uint64
var wg sync.WaitGroup

// NOTE: deliberately no global commit counter. In-place transactions never
// abort and their count is statically known, so counting them would just put a
// contended atomic on the hot path — the very bottleneck this mode removes.

func newObject(tag uint16, p unsafe.Pointer) *Object {
	return &Object{id: objSeq.Add(1), tag: tag, rec: p}
}

func (o *Object) lockSpin() {
	for !o.lock.CompareAndSwap(0, 1) {
		runtime.Gosched()
	}
}
func (o *Object) unlock() { o.lock.Store(0) }

// RunLocked{1,2,N}: acquire the footprint in object-id order (deadlock-free),
// run the body once, release. The body mutates records in place.

func RunLocked1(outer *Txn, o *Object, body func(*Txn)) {
	o.lockSpin()
	body(nil)
	o.unlock()
}

func RunLocked2(outer *Txn, o1, o2 *Object, body func(*Txn)) {
	a, b := o1, o2
	if a.id > b.id {
		a, b = b, a
	}
	a.lockSpin()
	dup := a == b // transfer(x, x): lock once
	if !dup {
		b.lockSpin()
	}
	body(nil)
	if !dup {
		b.unlock()
	}
	a.unlock()
}

func RunLockedN(outer *Txn, objs []*Object, body func(*Txn)) {
	for i := 1; i < len(objs); i++ {
		o := objs[i]
		j := i - 1
		for j >= 0 && objs[j].id > o.id {
			objs[j+1] = objs[j]
			j--
		}
		objs[j+1] = o
	}
	var locked []*Object
	for _, o := range objs {
		if len(locked) > 0 && locked[len(locked)-1] == o {
			continue // dedup
		}
		o.lockSpin()
		locked = append(locked, o)
	}
	body(nil)
	for i := len(locked) - 1; i >= 0; i-- {
		locked[i].unlock()
	}
}

func spawn(f func()) {
	wg.Add(1)
	go func() {
		defer wg.Done()
		f()
	}()
}

// (No native futures here either: a \`go\` statement spawns a fire-and-forget
// goroutine and \`Future\`/\`spawn\` are STM library types. In-place mode is all-2PL,
// so it never sees a \`retry\` — a \`retry\`-carrying txn is always OCC, forcing mvcc.)

// \`assert cond, msg\` — soft, like the interpreter's: it records the verdict and
// the program carries on, so a failing assertion in one goroutine does not hide
// what the others were going to do. There is no trace to record it in here, so
// the verdict goes to stderr and to the exit code, leaving stdout to the
// program's own \`print\`s.
var __assertFailed atomic.Bool

func __assert(ok bool, msg any) {
	if ok {
		return
	}
	__assertFailed.Store(true)
	text := __show(msg)
	if text == "" {
		fmt.Fprintln(os.Stderr, "assert FAILED")
		return
	}
	fmt.Fprintf(os.Stderr, "assert FAILED: %s\\n", text)
}

func __print(args ...any) {
	parts := make([]string, len(args))
	for i, a := range args {
		parts[i] = __show(a)
	}
	line := ""
	for i, p := range parts {
		if i > 0 {
			line += " "
		}
		line += p
	}
	fmt.Println(line)
}

func __sleep(sec float64) {
	if sec > 0 {
		time.Sleep(time.Duration(sec * float64(time.Second)))
	}
}

func __absI(x int64) int64 {
	if x < 0 {
		return -x
	}
	return x
}
// =================== end lock runtime ===================`;

// ---------------------------------------------------------------------------
// The handle runtime. Emitted (verbatim) only when a program uses a native
// handle — a socket (`uses.net`) or an open file (`uses.fileHandle`). Memory-mode
// agnostic, so it lives outside the two runtimes above and is appended once.
//
// STM's handle types (`TcpListener`/`TcpConn`/`File`) are value types wrapping an
// int; the real Go object (`net.Listener`/`net.Conn`/`*os.File`) is kept here in a
// small registry keyed by that int. So a handle crosses the STM/Go boundary as a
// plain int, and the net/file builtins (see `emitNet`/`emitFile` in builtins.ts)
// put/get/drop through here.
// ---------------------------------------------------------------------------

export const RUNTIME_HANDLES = `// ===================== handle runtime =====================
var __hMu sync.Mutex
// The std streams occupy the fixed handles 0/1/2 (the File values stdin/stdout/
// stderr carry); allocation starts above them so nothing else collides.
var __hSeq int64 = 2
var __hTab = map[int64]any{0: os.Stdin, 1: os.Stdout, 2: os.Stderr} // handle -> net.Listener | net.Conn | *os.File

// Register a native object and return its opaque int handle.
func __hPut(v any) int64 {
	__hMu.Lock()
	__hSeq++
	h := __hSeq
	__hTab[h] = v
	__hMu.Unlock()
	return h
}

// Resolve a handle back to its object (nil if unknown, e.g. after close).
func __hGet(h int64) any {
	__hMu.Lock()
	v := __hTab[h]
	__hMu.Unlock()
	return v
}

// Forget a handle once its object is closed, so the registry doesn't grow.
func __hDrop(h int64) {
	__hMu.Lock()
	delete(__hTab, h)
	__hMu.Unlock()
}
// =================== end handle runtime ===================`;

// ---------------------------------------------------------------------------
// The line-reader runtime. Emitted (verbatim) only when a program uses
// `readLine` (gogen sets `uses.readLine`) — it references `bufio`, imported on
// the same condition.
//
// `readLine` needs a *persistent* buffered reader per file handle: a fresh
// `bufio.Reader` each call would read ahead past the newline and drop the rest of
// the buffer. So each handle's reader is created once, on first `readLine`, and
// reused — keyed by the same int handle the registry uses.
// ---------------------------------------------------------------------------

export const RUNTIME_LINEREADER = `// ===================== line-reader runtime =====================
var __lrMu sync.Mutex
var __lineReaders = map[int64]*bufio.Reader{} // handle -> its buffered reader

// The buffered reader for handle h, created once over its *os.File (nil if the
// handle isn't an open file).
func __lineReader(h int64) *bufio.Reader {
	__lrMu.Lock()
	defer __lrMu.Unlock()
	if r := __lineReaders[h]; r != nil {
		return r
	}
	f, ok := __hGet(h).(*os.File)
	if !ok {
		return nil
	}
	r := bufio.NewReader(f)
	__lineReaders[h] = r
	return r
}
// =================== end line-reader runtime ===================`;
